import { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { api, ApiError } from "./api";
import { Modal, Spinner } from "./components";
import type { Project } from "./types";

export function ExportProject({
  projectId,
  before,
}: {
  projectId: string;
  before?: () => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <>
      <button
        className="secondary"
        disabled={busy}
        onClick={async () => {
          if (before && !(await before())) return;
          setBusy(true);
          setError("");
          try {
            const res = await fetch(`/api/projects/${projectId}/export`);
            if (!res.ok) throw new ApiError(res.status, await res.json());
            const url = URL.createObjectURL(await res.blob()),
              link = document.createElement("a");
            link.href = url;
            link.download = `gdtool-${projectId}.gdtool.gz`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? <Spinner /> : <Download size={15} />}Экспорт проекта
      </button>
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
    </>
  );
}
export function ImportProject({
  onImported,
}: {
  onImported: (p: Project) => void;
}) {
  const input = useRef<HTMLInputElement>(null),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <>
      <input
        ref={input}
        type="file"
        accept=".gz,.gdtool,application/gzip"
        hidden
        aria-label="Архив проекта"
        onChange={(e) => {
          setFile(e.target.files?.[0] || null);
          setError("");
          e.target.value = "";
        }}
      />
      <button className="secondary" onClick={() => input.current?.click()}>
        <Upload size={16} />
        Импорт проекта
      </button>
      {file && (
        <Modal
          title="Импорт проекта"
          close={() => {
            if (!busy) setFile(null);
          }}
        >
          <p>
            Файл <strong>{file.name}</strong> будет добавлен как отдельный
            проект со всеми материалами, версиями и плейтестами.
          </p>
          <p className="muted">
            Формат .gdtool.gz · файл до 75 МБ · содержимое до 100 МБ.
          </p>
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}
          <div className="modal-actions">
            <button
              className="secondary"
              disabled={busy}
              onClick={() => setFile(null)}
            >
              Отмена
            </button>
            <button
              className="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  const form = new FormData();
                  form.append("archive", file);
                  const p = await api<Project>("/projects/import", {
                    method: "POST",
                    body: form,
                  });
                  setFile(null);
                  onImported(p);
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Spinner /> : <Upload size={15} />}Импортировать копию
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
